/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useEffect } from 'react'
import { useInfiniteQuery, useMutation, useQuery } from 'react-query'

import type { DeleteUsersApiKeysRequest, RoleAssignments } from '@modules/cloud-api/v1/types'
import { queryClient } from '@modules/query'
import type { ApiErrorCollection, QueryHookOptions } from '@modules/query/types'
import { isRoleAssignmentsEmpty } from '@modules/role-assignments-lib'
import {
  createApiKey,
  deleteCurrentUserApiKey,
  deleteUsersApiKeys,
  getAllUsersApiKeys,
  getCurrentUserApiKeys,
} from '@modules/api-keys-api'
import type { DeleteApiKeyUrlParams } from '@modules/cloud-api/v1/urls'

import type { UseMutationOptions } from 'react-query'

export type CreateApiKeyParams = {
  name: string
  expiration: string | null
  roleAssignments?: RoleAssignments
}

/** Query hooks for fetching API keys **/

// Fetches all API keys using infinite query
// (this is necessary because the API endpoint returns a
// limit of 100 keys per page due to memory and performance concerns).
// Returns a flattened array of all keys for client-side querying, filtering and sorting.
export const useGetCurrentUserApiKeysQuery = (options?: QueryHookOptions) => {
  const query = useInfiniteQuery<
    Awaited<ReturnType<typeof getCurrentUserApiKeys>>,
    ApiErrorCollection
  >({
    queryKey: ['getCurrentUserApiKeys'],
    queryFn: ({ pageParam }) => getCurrentUserApiKeys(pageParam),
    ...options,
    getNextPageParam: (lastPage) => lastPage.next_page,
  })

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query

  // Automatically fetch all pages
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return {
    ...query,
    data: query.data?.pages.flatMap((page) => page.keys),
    // Set status as loading until all pages are fetched
    isLoading: query.isLoading || query.isFetchingNextPage,
  }
}

export const useGetAllUsersApiKeysQuery = (options?: QueryHookOptions) => {
  const query = useQuery<Awaited<ReturnType<typeof getAllUsersApiKeys>>, ApiErrorCollection>(
    ['getAllUsersApiKeys'],
    () => getAllUsersApiKeys(),
    options,
  )

  return {
    ...query,
    data: query.data?.keys,
  }
}

/** Mutation hooks for API key operations **/

export const useCreateApiKeyMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof createApiKey>>,
      ApiErrorCollection,
      CreateApiKeyParams
    >,
    'mutationFn'
  >,
) =>
  useMutation<Awaited<ReturnType<typeof createApiKey>>, ApiErrorCollection, CreateApiKeyParams>(
    ({ name, expiration, roleAssignments }) => {
      const payload = {
        description: name,
        ...(!isRoleAssignmentsEmpty(roleAssignments) && {
          role_assignments: roleAssignments,
        }),
        ...(expiration !== null && {
          expiration,
        }),
      }

      return createApiKey(payload)
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        options?.onSuccess?.(data, variables, context)
        // Invalidate both current user and all users API keys queries
        queryClient.invalidateQueries('getCurrentUserApiKeys')
        queryClient.invalidateQueries('getAllUsersApiKeys')
      },
    },
  )

export const useDeleteCurrentUserApiKeyMutation = () =>
  useMutation<
    Awaited<ReturnType<typeof deleteCurrentUserApiKey>>,
    ApiErrorCollection,
    DeleteApiKeyUrlParams
  >((payload: DeleteApiKeyUrlParams) => deleteCurrentUserApiKey(payload), {
    onSuccess: () => {
      // Invalidate both current user and all users API keys queries
      queryClient.invalidateQueries('getCurrentUserApiKeys')
      queryClient.invalidateQueries('getAllUsersApiKeys')
    },
  })

export const useDeleteUsersApiKeysMutation = () =>
  useMutation<
    Awaited<ReturnType<typeof deleteUsersApiKeys>>,
    ApiErrorCollection,
    DeleteUsersApiKeysRequest
  >((payload: DeleteUsersApiKeysRequest) => deleteUsersApiKeys(payload), {
    onSuccess: () => {
      // Invalidate both current user and all users API keys queries
      queryClient.invalidateQueries('getCurrentUserApiKeys')
      queryClient.invalidateQueries('getAllUsersApiKeys')
    },
  })
